<script lang="ts">
  import { goto } from '$app/navigation';
  import { page } from '$app/state';
  import {
    Bell,
    Home,
    Inbox,
    Landmark,
    LifeBuoy,
    LogOut,
    MessageSquareHeart,
    Monitor,
    Moon,
    Settings,
    Sun,
  } from 'lucide-svelte';
  import { get } from 'svelte/store';
  import type { ThemeMode } from '$lib/stores/theme.js';
  import { api } from '$lib/api.js';
  import { requestFeedback } from '$lib/posthog.js';
  import RulesIcon from '$lib/components/RulesIcon.svelte';
  import { auth } from '$lib/stores/auth.js';
  import { theme } from '$lib/stores/theme.js';
  import { resolveActiveTab } from '$lib/tabRouting.js';

  const tabs = [
    { href: '/dashboard', label: 'Home', icon: Home },
    { href: '/rules', label: 'Rules', icon: null },
    { href: '/activity', label: 'Activity', icon: Inbox },
    { href: '/accounts', label: 'Accounts', icon: Landmark },
    // subscriptions hidden — featureFlags.subscriptions = false
    { href: '/settings', label: 'Settings', icon: Settings },
    { href: '/notifications', label: 'Notifications', icon: Bell },
  ] as const;

  let currentPathname = $derived(page.url.pathname);

  let activeHref = $derived(
    resolveActiveTab(
      currentPathname,
      tabs.map((t) => t.href),
    ),
  );

  let themeMode = $state<ThemeMode>(get(theme));
  $effect(() => theme.subscribe((mode) => { themeMode = mode; }));

  const themeMenuLabel: Record<ThemeMode, string> = {
    light: 'Use dark mode',
    dark: 'Use system mode',
    system: 'Use light mode',
  };

  function active(href: string): boolean {
    if (tabs.some((t) => t.href === href)) return activeHref === href;
    return currentPathname === href || currentPathname.startsWith(`${href}/`);
  }

  async function handleLogout() {
    await api.auth.logout().catch(() => {});
    auth.logout();
    goto('/login');
  }
</script>

<aside
  class="hidden md:fixed md:inset-y-0 md:left-0 md:z-20 md:flex md:w-64 md:flex-col md:border-r md:border-border/60 md:bg-background/90 md:backdrop-blur-xs"
  aria-label="Main navigation"
>
  <div class="px-5 pb-5 pt-7">
    <a
      href="/dashboard"
      class="inline-flex items-center rounded-xl px-2 py-1 text-sm font-semibold tracking-tight text-foreground transition-colors hover:bg-muted/70"
    >
      AutoPaylot
    </a>
  </div>

  <nav class="flex-1 space-y-1 px-3 pb-4">
    {#each tabs as tab (tab.href)}
      {@const isActive = active(tab.href)}
      <a
        href={tab.href}
        data-sveltekit-preload-data="tap"
        aria-current={isActive ? 'page' : undefined}
        class="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors
          {isActive
            ? 'bg-brand-700 text-white shadow-[0_8px_20px_rgba(var(--shadow-tint),0.2)]'
            : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'}"
      >
        <span class="flex h-5 w-5 items-center justify-center">
          {#if tab.icon}
            {@const TabIcon = tab.icon}
            <TabIcon class="h-[1.1rem] w-[1.1rem]" strokeWidth={isActive ? 2.25 : 1.9} aria-hidden="true" />
          {:else}
            <RulesIcon class="h-[1.1rem] w-[1.1rem]" strokeWidth={isActive ? 2.25 : 1.9} />
          {/if}
        </span>
        <span>{tab.label}</span>
      </a>
    {/each}
  </nav>

  <div class="space-y-1 border-t border-border/60 px-3 pb-5 pt-3">
    <button
      type="button"
      onclick={() => theme.toggle()}
      class="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
    >
      <span class="flex h-5 w-5 items-center justify-center">
        {#if themeMode === 'light'}
          <Moon class="h-[1.1rem] w-[1.1rem]" strokeWidth={1.9} aria-hidden="true" />
        {:else if themeMode === 'dark'}
          <Monitor class="h-[1.1rem] w-[1.1rem]" strokeWidth={1.9} aria-hidden="true" />
        {:else}
          <Sun class="h-[1.1rem] w-[1.1rem]" strokeWidth={1.9} aria-hidden="true" />
        {/if}
      </span>
      <span>{themeMenuLabel[themeMode]}</span>
    </button>
    <a
      href="/help"
      data-sveltekit-preload-data="tap"
      aria-current={active('/help') ? 'page' : undefined}
      class="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors
        {active('/help')
          ? 'bg-brand-700 text-white shadow-[0_8px_20px_rgba(var(--shadow-tint),0.2)]'
          : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'}"
    >
      <span class="flex h-5 w-5 items-center justify-center">
        <LifeBuoy class="h-[1.1rem] w-[1.1rem]" strokeWidth={active('/help') ? 2.25 : 1.9} aria-hidden="true" />
      </span>
      <span>Help</span>
    </a>
    <button
      type="button"
      onclick={() => requestFeedback('desktop_side_nav')}
      class="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
    >
      <span class="flex h-5 w-5 items-center justify-center">
        <MessageSquareHeart class="h-[1.1rem] w-[1.1rem]" strokeWidth={1.9} aria-hidden="true" />
      </span>
      <span>Feedback</span>
    </button>
    <button
      type="button"
      onclick={handleLogout}
      class="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-400"
    >
      <span class="flex h-5 w-5 items-center justify-center">
        <LogOut class="h-[1.1rem] w-[1.1rem]" strokeWidth={1.9} aria-hidden="true" />
      </span>
      <span>Log out</span>
    </button>
  </div>
</aside>
